import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Select, message, InputNumber } from 'antd';
import { Pie, Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import 'antd/dist/reset.css';
import {
    faDownload,
    faExchangeAlt,
    faCreditCard,
    faSyncAlt,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { Button, NoInteractionView, Modal, Dropdown } from '@quyva/shared-ui';
import { getUrl } from './pdfDisplay/utils';
import { SUBSCRIPTION_PLANS, CREDIT_TOOLTIP, TOPUP_MIN_CREDITS, TOPUP_STEP_CREDITS } from '../utils/planUtils';
import { BillingFeatures, Features, isFeatureEnabled } from '@quyva/common';
import { useFeatureFlags, useGlobalCredits } from '../context';
import { useNavigate } from 'react-router-dom';
import SpendCategories from './usageDashboard/spendCategories';
import { usePlanData } from '../hooks/usePlanData';
import { loadTopupPaymentScript } from '../utils/loadTopupPaymentScript';
import { loadRenewalPaymentScript } from '../utils/loadRenewalPaymentScript';
import GlobalTimeFilter from './usageDashboard/GlobalTimeFilter';
const { Option } = Select;

// Top-up rate: $1 = 100 credits. Local-currency price is derived from the
// already-fetched exchange rate (currencyInfo.rate), not a flat assumption.
const isValidTopupCredits = (credits) =>
    Number.isInteger(credits) &&
    credits >= TOPUP_MIN_CREDITS &&
    credits % TOPUP_STEP_CREDITS === 0;

/** Format a price: symbol + whole number, e.g. $20, ₹1,670, €18 */
const fmtLocalPrice = (amount, currencyCode) => {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'narrowSymbol',
            maximumFractionDigits: 0,
        }).format(amount);
    } catch { return `${amount} ${currencyCode}`; }
};

const InvoiceStatus = Object.freeze({
    PENDING: 'PENDING',
    PAID: 'PAID',
    FAILED: 'Invoice Failed',
});

const SubscriptionStatus = Object.freeze({
    ACTIVE: 'Active',
    EXPIRED: 'Expired',
    PAID: 'Paid',
    UNPAID: 'Unpaid',
});
const CreditsLabel = ({ children }) => (
    <span title={CREDIT_TOOLTIP}>{children}</span>
);
// Round to 1 decimal and strip trailing float noise (e.g. 1227.8999… → 1227.9)
const fmtCredits = (val) => parseFloat((Number(val) || 0).toFixed(1));

// It will always be a date in midnight local time.
const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
    return new Date(y, m - 1, d);
};

const isCurrentSubscription = (subscription) => {
    if (!subscription?.is_active) return false;
    // A pre-created renewal successor is also is_active=true a day early (see
    // subscription_renewal_service.py) -- exclude rows that haven't started yet.
    if (subscription?.start_date) {
        const startDate = parseLocalDate(subscription.start_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (startDate > today) return false;
    }
    return true;
};

// Formats a date string into a localized short format (e.g., "Jan 15, 2024")
const formatDate = (dateString) => {
    if (!dateString) return '';
    return parseLocalDate(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

// Formats a date range into a standard format (e.g., "Nov 7, 2025 - Dec 7, 2025")
const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '';
    const start = parseLocalDate(startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    if (!endDate) return `${start} - Present`;
    const end = parseLocalDate(endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    return `${start} - ${end}`;
};

const chartOptions = {
    plugins: {
        legend: {
            display: false, // This disables the legend
        },
    },
};

const fmtCreditsTick = (v) => parseFloat(Number(v).toFixed(1));

// Separate chart options
const dailyBarOptions = {
    ...chartOptions,
    plugins: {
        ...chartOptions.plugins,
        tooltip: {
            callbacks: {
                label: (ctx) =>
                    ` Credits Used: ${Number(ctx.parsed.y).toFixed(1)}`,
            },
        },
    },
    scales: {
        x: {
            title: {
                display: true,
                text: 'Date',
                color: '#666',
                font: { size: 12 },
            },
        },
        y: {
            title: {
                display: true,
                text: 'Number of Credits',
                color: '#666',
                font: { size: 12 },
            },
            beginAtZero: true,
            ticks: {
                callback: (v) => Number(v).toFixed(1),
            },
        },
    },
};

const userBarOptions = {
    ...chartOptions,
    plugins: {
        ...chartOptions.plugins,
        tooltip: {
            callbacks: {
                label: (ctx) =>
                    ` Credits Used: ${Number(ctx.parsed.y).toFixed(1)}`,
            },
        },
    },
    scales: {
        x: {
            title: {
                display: true,
                text: 'Users',
                color: '#666',
                font: { size: 12 },
            },
        },
        y: {
            title: {
                display: true,
                text: 'Credits Used',
                color: '#666',
                font: { size: 12 },
            },
            beginAtZero: true,
            ticks: {
                callback: (v) => Number(v).toFixed(1),
            },
        },
    },
};

// Add new view type constant
const VIEW_TYPES = {
    DAILY: 'daily',
    MONTHLY: 'monthly',
    USERS: 'users',
    SPEND_CATEGORIES: 'spend_categories',
};

const SubscriptionDashboard = ({
    setManageSubscriptionModal,
    organizationId,
    organizationName,
    organizationOwner,
}) => {
    const { featureFlagsData } = useFeatureFlags();
    const { isSuperAdmin } = useGlobalCredits();
    const [subscriptions, setSubscriptions] = useState([]);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [currentPlanData, setCurrentPlanData] = useState(null);
    const [selectedValue, setSelectedValue] = useState(null);
    const [daily_data, setDailyData] = useState([]);
    const [invoiceData, setInvoiceData] = useState({});
    const user = organizationOwner
        ? { email: organizationOwner }
        : JSON.parse(localStorage.getItem('current'));
    const { currencyInfo: topupCurrencyInfo } = usePlanData(!organizationOwner ? user?.email : null);
    const [topupCredits, setTopupCredits] = useState(1000);
    const [topupLoading, setTopupLoading] = useState(false);
    const [topupReceipt, setTopupReceipt] = useState(null);
    const [renewalLoading, setRenewalLoading] = useState(false);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
    const [subscriptionHistory, setSubscriptionHistory] = useState([]);
    const [isPostpaid, setIsPostpaid] = useState(false);
    const [viewType, setViewType] = useState(VIEW_TYPES.DAILY);
    const [usersConsumption, setUsersConsumption] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    const [filteredDailyData, setFilteredDailyData] = useState([]);
    const [filteredUsersData, setFilteredUsersData] = useState([]);
    const [isOrgMember, setIsOrgMember] = useState(false);
    const [isOrgAdmin, setIsOrgAdmin] = useState(false);
    const [isOrgOwner, setIsOrgOwner] = useState(false);
    const [orgId, setOrgId] = useState(null);
    const [budgetInfo, setBudgetInfo] = useState(null);
    const [showMarkPaidConfirm, setShowMarkPaidConfirm] = useState(false);
    const [showTopupModal, setShowTopupModal] = useState(false);
    const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
    const [isRegeneratingInvoice, setIsRegeneratingInvoice] = useState(false);
    const [globalStartDate, setGlobalStartDate] = useState(null);
    const [globalEndDate, setGlobalEndDate] = useState(null);
    const navigate = useNavigate();

    const authToken = encodeURIComponent(
        JSON.parse(localStorage.getItem('current'))?.token || ''
    );

    // Add new effect to fetch organization details
    useEffect(() => {
        const fetchOrgDetails = async () => {
            try {
                const response = await axios.get(
                    getUrl(
                        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/details`
                    ),
                    {
                        headers: {
                            Authorization: encodeURIComponent(user.token),
                        },
                    }
                );

                if (response.data?.details) {
                    setIsOrgMember(true);
                    setOrgId(response.data.details.id);
                    setIsOrgAdmin(response.data.details.userRole === 'admin');
                    setIsOrgOwner(response.data.details.owner === user.email);
                } else {
                    setIsOrgMember(false);
                    setIsOrgAdmin(false);
                    setIsOrgOwner(false);
                }
            } catch (error) {
                console.error('Error fetching organization details:', error);
                setIsOrgMember(false);
                setIsOrgAdmin(false);
                setIsOrgOwner(false);
            }
        };

        if (user?.token) {
            fetchOrgDetails();
        }
    }, [user?.token]);

    useEffect(() => {
        const fetchBudget = async () => {
            try {
                const params = organizationId ? `?orgId=${organizationId}` : '';
                const res = await axios.get(
                    getUrl(
                        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/budget-used${params}`
                    ),
                    { headers: { Authorization: authToken } }
                );
                setBudgetInfo(res.data);
            } catch {
                /* non-critical */
            }
        };
        fetchBudget();
    }, [organizationId]);

    useEffect(() => {
        const fetchSubscriptionData = async () => {
            setLoadingSubscriptions(true);
            try {
                if (!user?.email) {
                    throw new Error('User email not found');
                }

                // Fetch subscription history first
                const historyResponse = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/subscription-history/${user.email}`
                    )
                );

                const historyData = historyResponse.data;
                setSubscriptionHistory(historyData.subscription_history);
                setIsPostpaid(!!historyData.is_postpaid);
                const firstHistory = historyData.subscription_history?.[0];
                if (firstHistory) {
                    setSelectedValue(firstHistory.id);
                }

                const showPersonalData = isOrgMember && !isOrgAdmin;
                const params = new URLSearchParams();
                params.append('show_personal_data_only', showPersonalData);
                params.append('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
                // Fetch current plan
                const response = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/current-plan/${user.email}?${params}`
                    )
                );

                const data = response.data;
                const transformedData = {
                    ...data,
                    // plan_name uses data.name (e.g. 'ENTERPRISE', 'PRO') for display and enum comparisons.
                    // data.plan_type ('PAY_AS_YOU_GO', 'TOP_PLAN') is available via ...data spread if needed.
                    plan_name: data.name || SUBSCRIPTION_PLANS.FREE,
                };

                // Match by the billing cycle's own primary key -- unambiguous,
                // unlike the old date+plan_name heuristic (needed because, after a
                // credit transfer, FREE and PRO org subs could share a calendar date).
                const currentHistory = historyData.subscription_history?.find(
                    (h) => h.id === transformedData.subscription_id
                );
                setSubscriptions([transformedData]);
                setCurrentPlanData(transformedData);
                setSelectedSubscription({
                    ...transformedData,
                    invoice_db_id: currentHistory?.invoice_db_id ?? null,
                    invoice_status: currentHistory?.invoice_status ?? null,
                    invoice_file: currentHistory?.invoice_id ?? null,
                    payment_status: currentHistory?.payment_status ?? null,
                });
                setSelectedValue(transformedData.subscription_id);
            } catch (error) {
                console.error(
                    'Error fetching subscription data:',
                    error.message
                );
                message.error('Failed to load subscription data');
                // Set default values
                const defaultData = {
                    plan_name: SUBSCRIPTION_PLANS.FREE,
                    queries_processed: 0,
                    total_queries_allowed: 0,
                    plan_queries_allowed: 0,
                    daily_data: [],
                };
                setSubscriptionHistory([]);
                setSubscriptions([defaultData]);
                setSelectedSubscription(defaultData);
                setDailyData([]);
            } finally {
                setLoadingSubscriptions(false);
            }
        };

        fetchSubscriptionData();
    }, [user.email, isOrgMember, isOrgAdmin, organizationId]);

    useEffect(() => {
        const fetchInvoiceData = async () => {
            if (
                !selectedSubscription?.id ||
                selectedSubscription.plan_name === SUBSCRIPTION_PLANS.FREE ||
                selectedSubscription.plan_name === SUBSCRIPTION_PLANS.ENTERPRISE ||
                !isSuperAdmin
            )
                return;

            try {
                if (!user?.email) {
                    throw new Error('User email not found');
                }

                const formData = new FormData();
                formData.append('email', user.email);
                formData.append(
                    'plan_id',
                    selectedSubscription.razorpay_plan_id
                );
                formData.append('start_date', selectedSubscription.start_date);
                if (selectedSubscription.invoice_file) {
                    formData.append('invoice_id', selectedSubscription.invoice_file);
                }
                if (selectedSubscription.razorpay_subscription_id) {
                    formData.append('subscription_id', selectedSubscription.razorpay_subscription_id);
                }

                const response = await axios.post(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/invoice`
                    ),
                    formData
                );

                const data = response.data;
                setInvoiceData(data || {});
            } catch (error) {
                console.error('Error fetching invoice data:', error.message);
                message.error('Failed to load invoice data');
                setInvoiceData({});
            }
        };

        fetchInvoiceData();
    }, [selectedSubscription, isSuperAdmin]);

    // Fetches per-user consumption for the globally-selected time range
    useEffect(() => {
        const fetchUsersConsumption = async () => {
            if (!isOrgMember && !isSuperAdmin) return;

            // For non-admin org users, always fetch personal data
            if (!isOrgAdmin && !isSuperAdmin && viewType === VIEW_TYPES.USERS)
                return;

            try {
                if (!user?.email) {
                    throw new Error('User email not found');
                }

                const params = new URLSearchParams();

                if (globalStartDate) {
                    params.append('start_date', globalStartDate.toISOString());
                }
                if (globalEndDate) {
                    params.append('end_date', globalEndDate.toISOString());
                }
                params.append('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);

                // Always add personal filter for non-admin, non-superadmin users
                if (!isOrgAdmin && !isSuperAdmin) {
                    params.append('personal', 'true');
                    params.append('user_email', user.email);
                }

                const url = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/org-users-consumption/${user.email}?${params}`
                );

                const response = await axios.get(url);

                const data = response.data;
                setUsersConsumption(data.users_consumption);
            } catch (error) {
                message.error('Failed to load users consumption data');
                setUsersConsumption([]);
            }
        };

        fetchUsersConsumption();
    }, [
        viewType,
        globalStartDate,
        globalEndDate,
        user?.email,
        isOrgAdmin,
        isOrgMember,
        isSuperAdmin,
    ]);

    // Fetches day-by-day usage for the globally-selected time range — powers the
    // Daily/Monthly Usage charts independently of the Subscription History dropdown.
    useEffect(() => {
        const fetchUsageDaily = async () => {
            if (!user?.email) return;

            try {
                const showPersonalData = isOrgMember && !isOrgAdmin;
                const params = new URLSearchParams();
                params.append('show_personal_data_only', showPersonalData);
                params.append('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
                if (globalStartDate) {
                    params.append('start_date', globalStartDate.toISOString());
                }
                if (globalEndDate) {
                    params.append('end_date', globalEndDate.toISOString());
                }

                const response = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/usage-daily/${user.email}?${params}`
                    )
                );

                const data = response.data;
                setDailyData(data.daily_data || []);
            } catch (error) {
                message.error('Failed to load usage analytics data');
                setDailyData([]);
            }
        };

        fetchUsageDaily();
    }, [globalStartDate, globalEndDate, user?.email, isOrgMember, isOrgAdmin]);

    const handleMarkInvoicePaid = (invoiceId) => {
        setPendingInvoiceId(invoiceId);
        setShowMarkPaidConfirm(true);
    };

    const doMarkInvoicePaid = async () => {
        const stableSelectedValue = selectedValue;
        const stablePendingId = pendingInvoiceId;
        try {
            const authToken = encodeURIComponent(user.token);
            await axios.post(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/toggle-payment-status`
                ),
                { invoiceId: stablePendingId },
                { headers: { Authorization: authToken } }
            );
            setShowMarkPaidConfirm(false);
            setPendingInvoiceId(null);
            message.success('Invoice marked as paid');
            // Re-fetch history to get updated invoice_status
            try {
                const historyResponse = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/subscription-history/${user.email}`
                    )
                );
                const historyData = historyResponse.data;
                setSubscriptionHistory(historyData.subscription_history);
                // Restore the period the user was viewing
                setSelectedValue(stableSelectedValue);
                // Patch invoice fields on the currently-viewed subscription
                const updated = historyData.subscription_history.find(
                    (h) => h.invoice_db_id === stablePendingId
                );
                if (updated) {
                    setSelectedSubscription((prev) => ({
                        ...prev,
                        invoice_db_id: updated.invoice_db_id,
                        invoice_status: updated.invoice_status,
                    }));
                }
            } catch {
                // Best-effort history refresh — original only acted on
                // historyResponse.ok, so a failed refresh is silently ignored.
            }
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Failed to mark invoice as paid'
            );
        }
    };

    const handleRegenerateInvoice = async (subscriptionId) => {
        const stableSelectedValue = selectedValue;
        setIsRegeneratingInvoice(true);
        try {
            const loggedInUser = JSON.parse(localStorage.getItem('current'));
            const formData = new FormData();
            formData.append('email', loggedInUser?.email || '');
            formData.append('subscription_id', subscriptionId);
            await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/regenerate-postpaid-invoice`
                ),
                formData
            );
            message.success('Invoice regenerated');
            // Re-fetch history to get the fresh invoice_status/invoice_file
            try {
                const historyResponse = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/subscription-history/${user.email}`
                    )
                );
                const historyData = historyResponse.data;
                setSubscriptionHistory(historyData.subscription_history);
                // Restore the period the user was viewing
                setSelectedValue(stableSelectedValue);
                const updated = historyData.subscription_history.find(
                    (h) => h.id === stableSelectedValue
                );
                if (updated) {
                    setSelectedSubscription((prev) => ({
                        ...prev,
                        invoice_db_id: updated.invoice_db_id,
                        invoice_status: updated.invoice_status,
                        invoice_file: updated.invoice_id ?? null,
                    }));
                }
            } catch {
                // Best-effort history refresh — original only acted on
                // historyResponse.ok, so a failed refresh is silently ignored.
            }
        } catch (error) {
            message.error(
                error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    'Failed to regenerate invoice'
            );
        } finally {
            setIsRegeneratingInvoice(false);
        }
    };

    const handleDownloadInvoice = async (id, displayName) => {
        try {
            if (!id) {
                message.warning('No invoice available for download');
                return;
            }

            const user = JSON.parse(localStorage.getItem('current'));
            if (!user?.token) {
                throw new Error('Authentication token not found');
            }

            // Check if user has admin rights if they belong to an organization
            if (user.organization_id) {
                const adminCheck = await axios.get(
                    getUrl(
                        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/details`
                    ),
                    {
                        headers: {
                            Authorization: encodeURIComponent(user.token),
                        },
                    }
                );

                if (adminCheck.data?.details?.userRole !== 'admin') {
                    message.error(
                        'Only organization administrators can download invoices'
                    );
                    return;
                }
            }

            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${
                    Config.NODE_SERVER_PORT
                }/download/${String(id).replace(/\.pdf$/, '')}`
            );

            const response = await axios.get(dynamicUrl, {
                headers: {
                    Authorization: encodeURIComponent(user.token),
                },
                responseType: 'blob',
            });

            if (response.status === 200) {
                const blob = response.data;
                const blobUrl = window.URL.createObjectURL(blob);
                const tempLink = document.createElement('a');
                tempLink.href = blobUrl;
                tempLink.setAttribute('download', displayName || `${id}.pdf`);
                tempLink.setAttribute('target', '_blank');
                document.body.appendChild(tempLink);
                tempLink.click();
                document.body.removeChild(tempLink);
                window.URL.revokeObjectURL(blobUrl);
                message.success('Invoice downloaded successfully');
            } else {
                throw new Error(`Failed to download file: ${response.status}`);
            }
        } catch (err) {
            message.error('Failed to download invoice');
        }
    };

    const handleTopupCheckout = async () => {
        const currency_code = topupCurrencyInfo.currency_code;
        const rate = topupCurrencyInfo.rate || 1;
        if (!isValidTopupCredits(topupCredits)) {
            message.error(`Credits must be at least ${TOPUP_MIN_CREDITS} and a multiple of ${TOPUP_STEP_CREDITS}`);
            return;
        }
        try {
            setTopupLoading(true);
            const res = await axios.post(
                getUrl(`http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/create-topup-checkout-session`),
                { credits: topupCredits, currency: currency_code, rate },
                { headers: { 'Content-Type': 'application/json', Authorization: authToken } }
            );
            if (res?.data) {
                // Modal stays open through the Razorpay popup — loading only
                // clears once the popup itself resolves (success/fail/dismiss),
                // not just because the order was created.
                loadTopupPaymentScript(
                    user,
                    {
                        amount:   res.data.amount,
                        order_id: res.data.order_id,
                        currency: res.data.currency,
                        credits:  res.data.credits,
                    },
                    {
                        onSuccess: (receipt) => {
                            setTopupReceipt(receipt);
                            setTopupLoading(false);
                            message.success('Top-up successful!');
                        },
                        onError: (errMsg) => {
                            message.error(errMsg || 'Payment failed. Please try again.');
                            setTopupLoading(false);
                        },
                        onDismiss: () => setTopupLoading(false),
                    }
                );
            } else {
                setTopupLoading(false);
            }
        } catch (err) {
            message.error(err.response?.data?.error || 'Failed to start top-up purchase');
            setTopupLoading(false);
        }
    };

    const handleRenewalCheckout = async () => {
        const currency_code = topupCurrencyInfo.currency_code;
        try {
            setRenewalLoading(true);
            const res = await axios.post(
                getUrl(`http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/create-renewal-checkout-session`),
                { currency: currency_code },
                { headers: { 'Content-Type': 'application/json', Authorization: authToken } }
            );
            if (res?.data) {
                loadRenewalPaymentScript(user, {
                    amount:   res.data.amount,
                    order_id: res.data.order_id,
                    currency: res.data.currency,
                });
            }
        } catch (err) {
            message.error(err.response?.data?.error || 'Failed to start renewal payment');
        } finally {
            setRenewalLoading(false);
        }
    };

    const handleDateChange = async (subscriptionId) => {
        try {
            setSelectedValue(subscriptionId);

            if (!user?.email) {
                throw new Error('User email not found');
            }

            const params = new URLSearchParams({
                subscription_id: subscriptionId,
            });

            const response = await axios.get(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/current-plan/${user.email}?${params}`
                )
            );

            const data = response.data;
            const transformedData = {
                ...data,
                plan_name:
                    data.name || data.plan_type || SUBSCRIPTION_PLANS.FREE,
            };

            // Merge invoice fields from history (match by primary key)
            const matchingHistory = subscriptionHistory.find(
                (h) => h.id === subscriptionId
            );

            // Clear invoice data when changing subscription
            setInvoiceData({});
            setSelectedSubscription({
                ...transformedData,
                invoice_db_id: matchingHistory?.invoice_db_id ?? null,
                invoice_status: matchingHistory?.invoice_status ?? null,
                invoice_file: matchingHistory?.invoice_id ?? null,
                payment_status: matchingHistory?.payment_status ?? null,
            });
        } catch (error) {
            console.error('Error changing subscription:', error);
            message.error('Failed to update subscription data');
        }
    };

    // Pie Chart Data
    const pieData = {
        labels: ['Credits Used', 'Credits Left'],

        datasets: [
            {
                data: [
                    fmtCredits(selectedSubscription?.queries_processed),
                    fmtCredits(
                        selectedSubscription?.plan_queries_allowed -
                            selectedSubscription?.queries_processed
                    ),
                ],
                backgroundColor: ['#03A9F4', '#F1F5FF'],
            },
        ],
    };

    useEffect(() => {
        setFilteredDailyData(daily_data);
        setFilteredUsersData(usersConsumption);
    }, [daily_data, usersConsumption]);

    // Separate data for daily usage
    const dailyBarData = {
        labels: filteredDailyData?.map((item) => {
            const date = parseLocalDate(item?.day);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
            });
        }),
        datasets: [
            {
                label: 'Credits Used',
                data: filteredDailyData?.map((item) =>
                    fmtCredits(item?.queries_processed)
                ),
                backgroundColor: '#03A9F4',
            },
        ],
    };

    // Separate data for user usage
    const userBarData = {
        labels: filteredUsersData.map((item) => item.email),
        datasets: [
            {
                label: 'User Consumption',
                data: filteredUsersData.map((item) =>
                    fmtCredits(item.queries_processed)
                ),
                backgroundColor: '#03A9F4',
            },
        ],
    };

    // Add effect to calculate monthly data when daily_data changes
    useEffect(() => {
        if (!daily_data?.length) return;

        const monthlyUsage = daily_data.reduce((acc, curr) => {
            const date = parseLocalDate(curr.day);
            const monthKey = `${date.getFullYear()}-${String(
                date.getMonth() + 1
            ).padStart(2, '0')}`;

            if (!acc[monthKey]) {
                acc[monthKey] = {
                    month: monthKey,
                    queries_processed: 0,
                };
            }
            acc[monthKey].queries_processed += curr.queries_processed;
            return acc;
        }, {});

        setMonthlyData(Object.values(monthlyUsage));
    }, [daily_data]);

    // Add monthly bar data
    const monthlyBarData = {
        labels: monthlyData?.map((item) => {
            const [year, month] = item.month.split('-');
            return new Date(year, month - 1).toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
            });
        }),
        datasets: [
            {
                label: 'Monthly Credits Used',
                data: monthlyData?.map((item) =>
                    fmtCredits(item.queries_processed)
                ),
                backgroundColor: '#03A9F4',
            },
        ],
    };

    if (loadingSubscriptions) {
        return <NoInteractionView viewText='Loading subscriptions' />;
    }

    if (!selectedSubscription) {
        return <NoInteractionView viewText='Loading subscription data' />;
    }

    return (
        <div className='subscription-dashboard relative'>
            <div
                className={`${organizationId ? 'h-full' : 'min-h-screen'} ${
                    organizationId ? 'p-4' : 'p-6'
                } bg-gray-50 flex flex-col overflow-y-auto`}
            >
                {/* Header Section - Show full version for non-org users or org admins */}
                <div className='w-full flex items-end justify-between mb-6'>
                    <div className='flex flex-col'>
                        <div className='flex items-center gap-4 mb-4'>
                            {/* Only show back button if not in a modal (i.e., has no organizationId) */}
                            {!organizationId && (
                                <button
                                    onClick={() => navigate('/chats')}
                                    className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white rounded-md border backgrounds-border hover:bg-gray-50 transition-colors'
                                >
                                    <svg
                                        className='mr-2 h-4 w-4'
                                        viewBox='0 0 20 20'
                                        fill='currentColor'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            d='M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z'
                                            clipRule='evenodd'
                                        />
                                    </svg>
                                    Back to Home
                                </button>
                            )}
                        </div>
                        {organizationName && (
                            <div className='flex items-center space-x-4 mb-2'>
                                <label className='text-lg font-medium text-backgrounds-primary300'>
                                    Organization:
                                </label>
                                <span className='text-lg font-semibold text-texts-secondary300'>
                                    {organizationName}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className='flex items-center gap-2'>
                        {!organizationOwner &&
                            (!isOrgMember || isOrgOwner) &&
                            isFeatureEnabled(
                                [
                                    Features.BILLING,
                                    BillingFeatures.MANAGE_SUBSCRIPTION,
                                ],
                                featureFlagsData
                            ) &&
                            isCurrentSubscription(selectedSubscription) &&
                            selectedSubscription?.plan_type === SUBSCRIPTION_PLANS.TOP_PLAN && (
                                <Button
                                    className='h-9 px-4'
                                    hoverText='Buy Top-up Credits'
                                    text='Buy Top-up Credits'
                                    icon={faCreditCard}
                                    type='default'
                                    onClick={() => setShowTopupModal(true)}
                                />
                            )}
                        {(!isOrgMember || isOrgAdmin) &&
                            !organizationId &&
                            isFeatureEnabled(
                                [
                                    Features.BILLING,
                                    BillingFeatures.MANAGE_SUBSCRIPTION,
                                ],
                                featureFlagsData
                            ) && (
                                <Button
                                    className='h-9 px-4 text-backgrounds-primary300'
                                    hoverText='Manage Subscription'
                                    text='Manage Subscription'
                                    icon={faExchangeAlt}
                                    type='success'
                                    onClick={() =>
                                        setManageSubscriptionModal(true)
                                    }
                                />
                            )}
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className='grid grid-cols-12 gap-6'>
                    {/* Left Column - Show for non-org users or org admins */}
                    {(!isOrgMember || isOrgAdmin) &&
                        isFeatureEnabled(
                            [
                                Features.BILLING,
                                BillingFeatures.CURRENT_PLAN_AND_OTHER_INFO,
                            ],
                            featureFlagsData
                        ) && (
                            <div className='col-span-4 flex flex-col gap-6'>
                                {(() => {
                                    // currentPlanData always reflects the true current
                                    // subscription (fresh /current-plan fetch); subscriptionHistory
                                    // is sorted by start_date, not by "is this actually active,"
                                    // so it can put a recently-cancelled plan ahead of the row
                                    // that's actually active now.
                                    const latestPeriod =
                                        currentPlanData || selectedSubscription || subscriptionHistory[0];
                                    const planNameDisplay =
                                        latestPeriod?.plan_name?.split('_')[0] || '';
                                    const isFree =
                                        latestPeriod?.plan_name ===
                                        SUBSCRIPTION_PLANS.FREE;
                                    const isCurrent = isCurrentSubscription(latestPeriod);
                                    const isPostpaidPeriod =
                                        !isFree && !latestPeriod?.razorpay_plan_id;

                                    let statusLabel = null;
                                    let statusColor = '';

                                    if (isSuperAdmin || isOrgAdmin || !isOrgMember) {
                                        if (isPostpaidPeriod || isCurrent) {
                                            statusLabel = SubscriptionStatus.ACTIVE;
                                            statusColor = 'text-green-600';
                                        } else {
                                            statusLabel = SubscriptionStatus.EXPIRED;
                                            statusColor = 'text-texts-red400';
                                        }
                                    }

                                    const isCurrentTopPlan =
                                        isCurrentSubscription(currentPlanData) &&
                                        currentPlanData?.plan_type === SUBSCRIPTION_PLANS.TOP_PLAN;
                                    const isUnpaid = isCurrentTopPlan && currentPlanData?.is_paid === false;
                                    const isRenewalDueSoon =
                                        isCurrentTopPlan &&
                                        currentPlanData?.is_paid !== false &&
                                        currentPlanData?.renewal_due_soon &&
                                        !currentPlanData?.next_cycle_paid;
                                    const isNextCyclePaid =
                                        isCurrentTopPlan &&
                                        currentPlanData?.is_paid !== false &&
                                        !!currentPlanData?.next_cycle_paid;
                                    // Always shown for a current Pro plan — never conditionally
                                    // hidden by timing. Disabled (with an explanatory tooltip)
                                    // until there's actually something to pay for.
                                    const showRenew = isCurrentTopPlan;
                                    const renewDisabled = renewalLoading || !(isUnpaid || isRenewalDueSoon);
                                    const renewLabel = isUnpaid ? 'Pay Now' : 'Renew';
                                    // 'info' renders as blue in this Button component (same
                                    // palette as 'success') — used for "payment required" so it
                                    // never reads as a destructive/red action.
                                    const renewType = isUnpaid ? 'info' : 'success';
                                    const renewTooltip = isUnpaid
                                        ? 'Payment required\nYour Pro plan cycle has ended without payment. Pay now to resume using credits.'
                                        : isRenewalDueSoon
                                        ? `Renews in ${currentPlanData?.days_until_renewal} day${currentPlanData?.days_until_renewal === 1 ? '' : 's'}\nPay now to renew ahead of time and continue seamlessly into your next cycle.`
                                        : isNextCyclePaid
                                        ? 'Next cycle already paid ✓'
                                        : 'Will be activated in the last 5 days before your cycle ends';

                                    return (
                                        <div className='bg-white shadow rounded-lg shadow px-5 py-4 flex items-center justify-between gap-3 flex-wrap'>
                                            <span className='text-base text-texts-secondary300'>
                                                Current Plan
                                            </span>
                                            <div className='flex items-center gap-3 flex-wrap justify-end'>
                                                <span className='text-base text-texts-tertiary400 font-medium'>
                                                    {planNameDisplay}
                                                    {statusLabel && (
                                                        <span
                                                            className={`ml-1 ${statusColor}`}
                                                        >
                                                            — {statusLabel}
                                                        </span>
                                                    )}
                                                </span>
                                                {showRenew && (
                                                    <Button
                                                        text={renewLabel}
                                                        type={renewType}
                                                        hoverText={renewTooltip}
                                                        disabled={renewDisabled}
                                                        onClick={handleRenewalCheckout}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}

                                <div className='bg-white shadow rounded-lg p-5 flex flex-col'>
                                {subscriptionHistory.length > 0 && (
                                    <div className='flex items-center justify-between mb-4 flex-none'>
                                        <label className='text-base font-medium text-backgrounds-primary300'>
                                            Billing Cycle
                                        </label>

                                        <Dropdown
                                            dropDownList={subscriptionHistory.map((sub) => ({
                                                label: formatDateRange(sub.start_date, sub.end_date),
                                                selected: selectedValue === sub.id,
                                                action: () => handleDateChange(sub.id),
                                            }))}
                                            showButtonText
                                            buttonText={
                                                selectedSubscription
                                                    ? formatDateRange(selectedSubscription.start_date, selectedSubscription.end_date)
                                                    : 'Select Date Range'
                                            }
                                            buttonClassName='w-fit px-3 h-9 flex items-center justify-center border border-backgrounds-border rounded-lg text-sm text-texts-secondary300'
                                        />
                                    </div>
                                )}

                                <div className='flex items-center justify-center gap-4 mb-4 flex-none'>
                                    <div className='w-[140px] h-[140px] flex-none'>
                                        <Pie
                                            data={pieData}
                                            options={{
                                                ...chartOptions,
                                                maintainAspectRatio: false,
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className='flex-none space-y-4 mt-4'>
                                    {selectedSubscription?.plan_name && (
                                        <div className='flex justify-between items-center'>
                                            <span className='text-base text-texts-secondary300'>
                                                Plan
                                            </span>
                                            <span className='text-base text-texts-tertiary400 font-medium'>
                                                {selectedSubscription.plan_name.split('_')[0]}
                                            </span>
                                        </div>
                                    )}
                                    {(isSuperAdmin || isOrgAdmin || !isOrgMember) &&
                                        (() => {
                                            const isCurrentSel = isCurrentSubscription(
                                                selectedSubscription
                                            );
                                            // See isPostpaidPeriod above for why this checks plan_name
                                            // instead of isOrgMember/isPostpaid.
                                            const isPostpaidPeriodSel =
                                                selectedSubscription?.plan_name !== SUBSCRIPTION_PLANS.FREE &&
                                                !selectedSubscription?.razorpay_plan_id;
                                            // Genuine prepaid (TOP_PLAN) row -- not FREE, not postpaid.
                                            const isPrepaidPeriodSel =
                                                selectedSubscription?.plan_name !== SUBSCRIPTION_PLANS.FREE &&
                                                !!selectedSubscription?.razorpay_plan_id;

                                            // A postpaid cycle becomes eligible for the invoice regenerate button once it has ENDED (end_date < today) not dependedent on is_active
                                            const cycleToday = new Date();
                                            cycleToday.setHours(0, 0, 0, 0);
                                            const hasEnded =
                                                !!selectedSubscription?.end_date &&
                                                parseLocalDate(selectedSubscription.end_date) < cycleToday;

                                            let statusLabel, statusColor;
                                            let showMarkAsPaid = false;
                                            let showRegenerate = false;

                                            if (isPostpaidPeriodSel && hasEnded) {
                                                const invoiceNotReady =
                                                    !selectedSubscription?.invoice_status ||
                                                    !selectedSubscription?.invoice_file;
                                                if (invoiceNotReady) {
                                                    if (isSuperAdmin) {
                                                        statusLabel = InvoiceStatus.FAILED;
                                                        statusColor = 'text-texts-red400';
                                                        showRegenerate = true;
                                                    } else {
                                                        statusLabel = InvoiceStatus.PENDING;
                                                        statusColor = 'text-texts-secondary300';
                                                    }
                                                } else {
                                                    statusLabel = selectedSubscription.invoice_status;
                                                    statusColor =
                                                        statusLabel === InvoiceStatus.PENDING
                                                            ? 'text-texts-secondary300'
                                                            : 'text-green-600';
                                                    if (statusLabel === InvoiceStatus.PENDING && isSuperAdmin) {
                                                        showMarkAsPaid = true;
                                                    }
                                                }
                                            } else if (isPrepaidPeriodSel) {
                                                // Paid/Unpaid is a fact about this specific cycle row
                                                // (is_paid), independent of whether it's the currently
                                                // active one -- a lapsed-unpaid current row and a
                                                // historically-unpaid past row both show Unpaid.
                                                const isPaidVal = selectedSubscription?.is_paid !== false;
                                                statusLabel = isPaidVal
                                                    ? SubscriptionStatus.PAID
                                                    : SubscriptionStatus.UNPAID;
                                                statusColor = isPaidVal
                                                    ? 'text-green-600'
                                                    : 'text-texts-red400';
                                            } else if (!isCurrentSel) {
                                                statusLabel = SubscriptionStatus.EXPIRED;
                                                statusColor = 'text-texts-red400';
                                            }
                                            if (!statusLabel) return null;

                                            return (
                                                <div className='flex justify-between items-center'>
                                                    <span className='text-base text-texts-secondary300'>
                                                        Status
                                                    </span>
                                                    <div className='flex items-center gap-2 flex-wrap justify-end'>
                                                        <span
                                                            className={`text-base font-medium ${statusColor}`}
                                                        >
                                                            {statusLabel}
                                                        </span>
                                                        {showMarkAsPaid && (
                                                            <Button
                                                                text='Mark as Paid'
                                                                type='success'
                                                                onClick={() =>
                                                                    handleMarkInvoicePaid(
                                                                        selectedSubscription.invoice_db_id
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {showRegenerate && (
                                                            <Button
                                                                icon={
                                                                    isRegeneratingInvoice
                                                                        ? faSpinner
                                                                        : faSyncAlt
                                                                }
                                                                hoverText={
                                                                    isRegeneratingInvoice
                                                                        ? 'Regenerating…'
                                                                        : 'Regenerate Invoice'
                                                                }
                                                                type='danger'
                                                                disabled={isRegeneratingInvoice}
                                                                onClick={() =>
                                                                    handleRegenerateInvoice(
                                                                        selectedSubscription.subscription_id
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    {selectedSubscription?.plan_name !==
                                        SUBSCRIPTION_PLANS.FREE && (
                                        <>
                                            {isCurrentSubscription(
                                                selectedSubscription
                                            ) && (
                                                <>
                                                    <div className='flex justify-between items-center'>
                                                        <span className='text-base text-texts-secondary300'>
                                                            Started On
                                                        </span>
                                                        <span className='text-base text-texts-tertiary400 font-medium'>
                                                            {formatDate(
                                                                selectedSubscription?.start_date
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className='flex justify-between items-center'>
                                                        <span className='text-base text-texts-secondary300'>
                                                            Active Till
                                                        </span>
                                                        <span className='text-base text-texts-tertiary400 font-medium'>
                                                            {formatDate(
                                                                selectedSubscription?.end_date
                                                            )}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            
                                            {/* invoiceData.invoice_id  — prepaid:  subscription_transaction.invoice_id (SA fetch via /invoice)
                                                invoiceData.invoice_file — postpaid: postpaid_invoices.invoice_file  (SA fetch via /organization/invoices)
                                                selectedSubscription.invoice_file — OA fallback: subscription_transaction.invoice_id via history API */}
                                            {(isSuperAdmin ||
                                                (isOrgAdmin && !isPostpaid) ||
                                                !isOrgMember) &&
                                                selectedSubscription?.is_paid !== false &&
                                                (invoiceData?.invoice_id ||
                                                    invoiceData?.invoice_file ||
                                                    selectedSubscription?.invoice_file) && (
                                                    <div className='flex justify-between items-center'>
                                                        <span className='text-base text-texts-secondary300'>
                                                            Invoice
                                                        </span>
                                                        <Button
                                                            className='text-backgrounds-primary300'
                                                            icon={faDownload}
                                                            onClick={() =>
                                                                handleDownloadInvoice(
                                                                    selectedSubscription?.invoice_db_id ||
                                                                        invoiceData?.id ||
                                                                        invoiceData?.invoice_id ||
                                                                        invoiceData?.invoice_file ||
                                                                        selectedSubscription?.invoice_file,
                                                                    invoiceData?.invoice_id ||
                                                                        invoiceData?.invoice_file ||
                                                                        selectedSubscription?.invoice_file
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )}
                                        </>
                                    )}
                                    <div className='flex justify-between items-center'>
                                        <span className='text-base text-texts-secondary300'>
                                            <CreditsLabel>
                                                Total Credits Used
                                            </CreditsLabel>
                                        </span>
                                        <span className='text-base text-texts-tertiary400 font-medium'>
                                            {fmtCredits(
                                                selectedSubscription?.queries_processed
                                            )}
                                            /
                                            {fmtCredits(
                                                selectedSubscription?.plan_queries_allowed
                                            )}
                                        </span>
                                    </div>
                                    {/* Top-up credits are a live wallet balance, not tied to any
                                        one billing period — only show for the current subscription,
                                        never when browsing a past period from the history dropdown. */}
                                    {isCurrentSubscription(selectedSubscription) &&
                                        fmtCredits(selectedSubscription?.topup_credits_remaining) > 0 && (
                                        <div className='flex justify-between items-center'>
                                            <span className='text-base text-texts-secondary300'>
                                                <CreditsLabel>
                                                    Top-up Credits Remaining
                                                </CreditsLabel>
                                            </span>
                                            <span className='text-base text-texts-tertiary400 font-medium'>
                                                {fmtCredits(
                                                    selectedSubscription?.topup_credits_remaining
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    {budgetInfo &&
                                        selectedSubscription?.is_active &&
                                        isSuperAdmin &&
                                        isPostpaid &&
                                        selectedSubscription?.plan_name !==
                                            SUBSCRIPTION_PLANS.FREE && (
                                            <div className='flex justify-between items-center'>
                                                <span className='text-base text-texts-secondary300'>
                                                    Budget Used
                                                </span>
                                                <span
                                                    className='text-base font-semibold text-texts-tertiary400 cursor-pointer underline underline-offset-2'
                                                    title={`${fmtCredits(
                                                        budgetInfo.total_credits
                                                    )} credits × ${
                                                        budgetInfo.currency ===
                                                        'USD'
                                                            ? '$'
                                                            : ''
                                                    }${budgetInfo.rate_per_credit.toFixed(
                                                        4
                                                    )}/credit${
                                                        budgetInfo.currency !==
                                                        'USD'
                                                            ? ` (1 USD = ${budgetInfo.exchange_rate.toFixed(
                                                                  2
                                                              )} ${
                                                                  budgetInfo.currency
                                                              })`
                                                            : ''
                                                    }`}
                                                >
                                                    {new Intl.NumberFormat(
                                                        undefined,
                                                        {
                                                            style: 'currency',
                                                            currency:
                                                                budgetInfo.currency,
                                                            currencyDisplay:
                                                                'narrowSymbol',
                                                            maximumFractionDigits: 2,
                                                        }
                                                    ).format(
                                                        budgetInfo.cost_local
                                                    )}
                                                    <span className='text-xs text-gray-400 ml-1'>
                                                        (approx.)
                                                    </span>
                                                </span>
                                            </div>
                                        )}
                                    {/* Actual vs Margin breakdown — super admin, postpaid only */}
                                    {isSuperAdmin &&
                                        isPostpaid &&
                                        selectedSubscription?.plan_name !==
                                            SUBSCRIPTION_PLANS.FREE &&
                                        selectedSubscription?.queries_processed >
                                            0 && (
                                            <div className='flex flex-col gap-1 pl-2 border-l-2 border-blue-100'>
                                                <div className='flex justify-between items-center'>
                                                    <span className='text-sm text-texts-secondary300'>
                                                        LLM cost
                                                    </span>
                                                    <span className='text-sm text-green-600 font-medium'>
                                                        {fmtCredits(
                                                            selectedSubscription?.actual_cost_total
                                                        )}{' '}
                                                        credits
                                                    </span>
                                                </div>
                                                <div className='flex justify-between items-center'>
                                                    <span className='text-sm text-texts-secondary300'>
                                                        Platform cost
                                                    </span>
                                                    <span className='text-sm text-orange-500 font-medium'>
                                                        {fmtCredits(
                                                            selectedSubscription?.margin_cost_total
                                                        )}{' '}
                                                        credits
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    <div className='flex justify-between items-center'>
                                        <span className='text-base text-texts-secondary300'>
                                            <CreditsLabel>
                                                Daily Average Usage
                                                (credits/day)
                                            </CreditsLabel>
                                        </span>
                                        <span className='text-base text-texts-tertiary400 font-medium'>
                                            {(() => {
                                                const startDate = parseLocalDate(
                                                    selectedSubscription?.start_date
                                                );
                                                const endDate =
                                                    isCurrentSubscription(
                                                        selectedSubscription
                                                    )
                                                        ? new Date()
                                                        : parseLocalDate(
                                                              selectedSubscription?.end_date
                                                          );
                                                const daysDiff = Math.max(
                                                    1,
                                                    Math.floor(
                                                        (endDate - startDate) /
                                                            (1000 *
                                                                60 *
                                                                60 *
                                                                24)
                                                    ) + 1
                                                );
                                                const average = fmtCredits(
                                                    selectedSubscription?.queries_processed /
                                                        daysDiff
                                                );
                                                return `${average}`;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                                </div>
                            </div>
                        )}

                    {/* Right Column - Adjust width based on user type */}
                    {isFeatureEnabled(
                        [
                            Features.BILLING,
                            BillingFeatures.USAGE_ANALYTICS_GRAPH,
                        ],
                        featureFlagsData
                    ) && (
                        <div
                            className={`${
                                !isOrgMember || isOrgAdmin
                                    ? 'col-span-8'
                                    : 'col-span-12'
                            } bg-white shadow rounded-lg p-6 flex flex-col`}
                        >
                            {isOrgMember &&
                                !isOrgAdmin &&
                                selectedSubscription?.organization_name && (
                                    <div className='mb-4 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 text-sm text-texts-tertiary400'>
                                        You are a member of{' '}
                                        <span className='font-semibold'>
                                            {
                                                selectedSubscription.organization_name
                                            }
                                        </span>
                                        . Usage shown is your personal
                                        contribution within the organization.
                                    </div>
                                )}
                            <div className='flex justify-between items-center mb-4'>
                                <h3 className='text-lg font-medium text-backgrounds-primary300'>
                                    {isOrgMember && !isOrgAdmin
                                        ? 'Your Usage Analytics'
                                        : 'Usage Analytics'}
                                </h3>
                                {(!isOrgMember || isOrgAdmin) && (
                                    <div className='flex items-center gap-4'>
                                        <GlobalTimeFilter
                                            onRangeChange={(start, end) => {
                                                setGlobalStartDate(start);
                                                setGlobalEndDate(end);
                                            }}
                                        />
                                        {selectedSubscription?.plan_name !==
                                            SUBSCRIPTION_PLANS.FREE &&
                                            (!selectedSubscription?.organization_id || isOrgAdmin || isSuperAdmin) && (
                                                <Select
                                                    value={viewType}
                                                    onChange={setViewType}
                                                    className='w-38'
                                                >
                                                    <Option
                                                        value={VIEW_TYPES.DAILY}
                                                    >
                                                        Daily Usage
                                                    </Option>
                                                    {selectedSubscription?.organization_id && (
                                                        <Option
                                                            value={VIEW_TYPES.USERS}
                                                        >
                                                            Per User Usage
                                                        </Option>
                                                    )}
                                                    <Option
                                                        value={
                                                            VIEW_TYPES.SPEND_CATEGORIES
                                                        }
                                                    >
                                                        Spend Categories
                                                    </Option>
                                                </Select>
                                            )}
                                    </div>
                                )}
                            </div>
                            <div
                                className={`${
                                    viewType === VIEW_TYPES.SPEND_CATEGORIES
                                        ? 'flex-1 min-h-[400px]'
                                        : 'h-[400px]'
                                } flex flex-col min-h-0`}
                            >
                                {viewType === VIEW_TYPES.DAILY ? (
                                    <Bar
                                        data={dailyBarData}
                                        options={{
                                            ...dailyBarOptions,
                                            maintainAspectRatio: false,
                                            responsive: true,
                                        }}
                                    />
                                ) : viewType === VIEW_TYPES.MONTHLY ? (
                                    <Bar
                                        data={monthlyBarData}
                                        options={{
                                            ...dailyBarOptions,
                                            maintainAspectRatio: false,
                                            responsive: true,
                                        }}
                                    />
                                ) : viewType === VIEW_TYPES.SPEND_CATEGORIES ? (
                                    <SpendCategories
                                        email={user?.email}
                                        orgId={
                                            isOrgAdmin
                                                ? orgId
                                                : isSuperAdmin && organizationId
                                                ? organizationId
                                                : null
                                        }
                                        startDate={globalStartDate}
                                        endDate={globalEndDate}
                                        isSuperAdmin={isSuperAdmin}
                                        isOrgAdmin={isOrgAdmin}
                                    />
                                ) : (
                                    <Bar
                                        data={userBarData}
                                        options={{
                                            ...userBarOptions,
                                            maintainAspectRatio: false,
                                            responsive: true,
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {showMarkPaidConfirm && (
                    <Modal
                        title='Confirm Payment Status Change'
                        titleIcon={faCreditCard}
                        onClose={() => {
                            setShowMarkPaidConfirm(false);
                            setPendingInvoiceId(null);
                        }}
                        className='w-[500px]'
                    >
                        <div className='p-4'>
                            <p className='mb-4'>
                                Are you sure you want to mark this invoice as
                                paid?
                            </p>
                            <p className='mb-6 text-sm text-texts-secondary300'>
                                This will update the payment status from
                                "Pending" to "Paid" and cannot be automatically
                                reversed.
                            </p>
                            <div className='flex justify-end gap-3'>
                                <Button
                                    text='Cancel'
                                    type='danger'
                                    onClick={() => {
                                        setShowMarkPaidConfirm(false);
                                        setPendingInvoiceId(null);
                                    }}
                                />
                                <Button
                                    text='Confirm'
                                    type='success'
                                    onClick={doMarkInvoicePaid}
                                />
                            </div>
                        </div>
                    </Modal>
                )}

                {showTopupModal && (
                    <Modal
                        title={topupReceipt ? 'Top-up Successful' : 'Buy Top-up Credits'}
                        titleIcon={faCreditCard}
                        onClose={() => {
                            setShowTopupModal(false);
                            // A completed purchase changed credit balances elsewhere on
                            // this page (plan card, usage charts) -- reload so they're
                            // not left stale now that there's no post-payment redirect.
                            if (topupReceipt) window.location.reload();
                            setTopupReceipt(null);
                        }}
                        className='w-[420px]'
                    >
                        <div className='p-4'>
                            {topupReceipt ? (
                                <>
                                    <p className='mb-4 text-sm text-texts-secondary300'>
                                        {topupReceipt.credits
                                            ? `${Number(topupReceipt.credits).toLocaleString()} credits`
                                            : 'Your credits'} have been added to your account.
                                    </p>
                                    <div className='flex justify-end gap-3 mt-4'>
                                        {topupReceipt.invoice_filename && (
                                            <Button
                                                text='Download Receipt'
                                                type='default'
                                                icon={faDownload}
                                                onClick={() =>
                                                    handleDownloadInvoice(
                                                        topupReceipt.invoice_filename,
                                                        topupReceipt.invoice_filename
                                                    )
                                                }
                                            />
                                        )}
                                        <Button
                                            text='Done'
                                            type='success'
                                            onClick={() => {
                                                setShowTopupModal(false);
                                                setTopupReceipt(null);
                                                window.location.reload();
                                            }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className='mb-4 text-sm text-texts-secondary300'>
                                        Buy extra credits that never expire. They're used automatically once your plan's credits run out.
                                    </p>
                                    <div className='flex items-center gap-2 mb-1'>
                                        <InputNumber
                                            min={TOPUP_MIN_CREDITS}
                                            step={TOPUP_STEP_CREDITS}
                                            value={topupCredits}
                                            onChange={(val) => setTopupCredits(val)}
                                            addonAfter='credits'
                                            className='w-40'
                                            autoFocus
                                        />
                                        <span className='text-sm text-blue-600 font-medium'>
                                            = {fmtLocalPrice(
                                                (topupCredits / 100) * (topupCurrencyInfo.rate || 1),
                                                topupCurrencyInfo.currency_code || 'USD'
                                            )}
                                        </span>
                                    </div>
                                    {!isValidTopupCredits(topupCredits) && (
                                        <div className='text-xs text-red-500 mb-2'>
                                            Must be at least {TOPUP_MIN_CREDITS} and a multiple of {TOPUP_STEP_CREDITS}
                                        </div>
                                    )}
                                    <div className='flex justify-end gap-3 mt-4'>
                                        <Button
                                            text='Cancel'
                                            type='default'
                                            onClick={() => setShowTopupModal(false)}
                                        />
                                        <Button
                                            text={topupLoading ? 'Processing…' : 'Buy Credits'}
                                            type='success'
                                            onClick={handleTopupCheckout}
                                            disabled={topupLoading || !isValidTopupCredits(topupCredits)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </Modal>
                )}

            </div>
        </div>
    );
};

export default SubscriptionDashboard;
